// @ts-ignore
import { UseFormMethods } from 'react-hook-form'

import { TZDate } from '@date-fns/tz'
import tzlookup from '@photostructure/tz-lookup'
import { addDays, format, getDayOfYear, setDayOfYear } from 'date-fns'

import {
  DevicesAlarmQuery,
  DevicesCameraQuery,
  DevicesDoorQuery,
  FacilityTypesQuery,
  FloorsByFacilityIdsQuery,
  ManualIncidentCategoriesQuery,
  RecipientsByRuleQuery,
  RuleDevicesQuery,
} from '@/graphql/generated/operations'
import {
  Action,
  CreateRuleLogicInput,
  DeviceType,
  RepeatInterval,
  RuleEventType,
} from '@/graphql/generated/schemas'
import { getUniqueValues } from '@/utils/filterDrawers'
import { formatFullName } from '@/utils/formats'
import {
  OptionI,
  getIncidentCategoryOptions,
} from '@/utils/forms/optionBuilders'
import { sortOptions } from '@/utils/forms/optionHelpers'
import { isAIOperator } from '@/utils/incidents'

import {
  CombinedUsersI,
  CreatableOptionI,
  DayOptionI,
  FormInputsT,
  OptionT,
  RuleLogicsT,
  RuleRecipientsT,
} from '../types/types'

export const MENU_HEIGHT = 360
export const MAX_RULENAME_LENGTH = 155
const defaultFloorName = 'No floor name provided'
const defaultDoorName = 'No door name provided'
const defaultCameraName = 'No camera name provided'
const defaultAlarmName = 'No alarm name provided'

export const getOptionValues = <T>(options: OptionI<T>[]): T[] =>
  options?.map((opt) => opt.value)

export const ruleEventTypeOptions: OptionI<RuleEventType>[] = [
  { label: 'Alarm', value: RuleEventType.Alarm },
  { label: 'Door Forced', value: RuleEventType.DoorForcedOpen },
  { label: 'Door Held Open', value: RuleEventType.DoorHeldOpen },
  { label: 'Door Open Alarm', value: RuleEventType.DoorOpenAlarm },
  { label: 'Tailgate', value: RuleEventType.Tailgating },
  { label: 'Line Crossing', value: RuleEventType.LineCrossing },
]

export const actionOptions: OptionI<Action>[] = [
  { label: 'Mute', value: Action.Mute },
  { label: 'Enable AI Operator', value: Action.EnableAiOperator },
  { label: 'Email', value: Action.SendEmail },
]
export const repeatTypeOptions: OptionI<RepeatInterval>[] = [
  { label: 'Day', value: RepeatInterval.Daily },
  { label: 'Week', value: RepeatInterval.Weekly },
]

export const DAYS: DayOptionI[] = [
  {
    label: 'S',
    value: 'Su',
    selected: false,
  },
  {
    label: 'M',
    value: 'Mo',
    selected: false,
  },
  {
    label: 'T',
    value: 'Tu',
    selected: false,
  },
  {
    label: 'W',
    value: 'We',
    selected: false,
  },
  {
    label: 'T',
    value: 'Th',
    selected: false,
  },
  {
    label: 'F',
    value: 'Fr',
    selected: false,
  },
  {
    label: 'S',
    value: 'Sa',
    selected: false,
  },
]

// Checks if the Email Action is selected and if at least one email is a selected
export const isEmailActionValid = (
  action: Action,
  emails: CreatableOptionI[]
): boolean => {
  const isEmailActionSelected = action === Action.SendEmail
  const areEmailsSelected = (emails?.length ?? 0) > 0
  return isEmailActionSelected ? areEmailsSelected : true
}

// Checks that the event options selected are valid events for a door device if the mute option is selected
// I.e Line crossing is not a valid event type to be selected for a door device
export const areValidEventsForDoorDevice = (
  selectedEventOptions: OptionI<RuleEventType>[],
  selectedAction: Action
): boolean =>
  selectedEventOptions?.some(
    (ev) =>
      ev.value === RuleEventType.DoorHeldOpen ||
      ev.value === RuleEventType.DoorForcedOpen ||
      ev.value === RuleEventType.DoorOpenAlarm ||
      ev.value === RuleEventType.Tailgating
  ) || selectedAction !== Action.Mute

export const getSelectedDaysAsString = (days: DayOptionI[]): string => {
  return days?.flatMap((f) => (f.selected ? f.value : []))?.join('')
}

export const getFloorByFacilityIdsOptions = (
  floorsData: FloorsByFacilityIdsQuery
): OptionI[] => {
  const edges = floorsData?.floors?.edges
  if (!edges) return []
  return (
    [...edges]
      ?.sort(
        (a, b) =>
          a?.node?.facility?.name?.localeCompare(b?.node?.facility?.name) ||
          a?.node?.name?.localeCompare(b?.node?.name)
      )
      .map((f) => {
        const name = f?.node?.name ? f?.node?.name : defaultFloorName
        return {
          label: `${name} - ${f?.node?.facility?.name}`,
          value: f?.node?.id,
        }
      }) || []
  )
}

export const getDevicesByFacilityIdsOptions = (
  data: DevicesDoorQuery
): OptionI[] => {
  const edges = data?.devices?.edges
  if (!edges) return []
  return (
    [...edges]
      ?.sort((a, b) => a?.node?.name?.localeCompare(b?.node?.name))
      .map((f) => {
        const name = f?.node?.name ? f?.node?.name : defaultDoorName
        return {
          label: `${name} - ${f?.node?.facility?.name}`,
          value: f?.node?.id,
        }
      }) || []
  )
}
export const getCamerasOptions = (data: DevicesCameraQuery): OptionI[] => {
  const edges = data?.devices?.edges
  if (!edges) return []
  return (
    edges?.map((f) => {
      const name = f?.node?.name ?? defaultCameraName
      return {
        label: `${name} - ${f?.node?.facility?.name}`,
        value: f?.node?.id,
      }
    }) || []
  )
}

export const getAlarmsOptions = (data: DevicesAlarmQuery): OptionI[] => {
  const edges = data?.devices?.edges
  if (!edges) return []
  return (
    [...edges]
      ?.sort((a, b) => a?.node?.name?.localeCompare(b?.node?.name))
      .map((f) => {
        const name = f?.node?.name ? f?.node?.name : defaultAlarmName
        return {
          label: `${name} - ${f?.node?.facility?.name}`,
          value: f?.node?.id,
        }
      }) || []
  )
}

export const getRuleEmails = (data: RuleRecipientsT): OptionI[] => {
  const recipients =
    data?.recipients?.edges
      .map((e) => ({ id: e.node.userId, email: e.node.email }))
      .filter((r) => r.id === null) || []
  const users = data?.users?.edges.map((e) => e.node) || []
  const combinedUsers: CombinedUsersI[] = [...recipients, ...users]

  if (!combinedUsers) return []
  return (
    [...combinedUsers]
      ?.sort((a, b) => a?.email?.localeCompare(b?.email))
      ?.filter((u) => u.email !== null && !isAIOperator(u))
      .map((u) => {
        const hasFullName = u?.firstName && u?.lastName
        const fullName = hasFullName ? `(${formatFullName(u)})` : ''
        return {
          label: `${u.email} ${fullName}`,
          value: u.id,
        }
      }) || []
  )
}

export const getRuleLogicInput = (
  deviceIds: string[]
): CreateRuleLogicInput => {
  if (deviceIds?.length > 0) {
    const children = deviceIds.map((id) => ({ value: id }))
    return { children }
  }
  return {}
}

// Adjusts the date by the passed timeZone.
export const getTimeInUTC = (date: Date, timeZone: string): string => {
  // Remove the timeZone offset from the TimePicker component (This uses the users local date)
  const timePickerOffset = new Date().getTimezoneOffset()
  const timePickerDate = new Date()
  timePickerDate.setTime(date?.getTime() - timePickerOffset * 60000)
  // Convert the time to utc, adding the offset for the provided timeZone
  const newTZDate = new TZDate(timePickerDate, timeZone)
  const utfcOffset = newTZDate.getTimezoneOffset()
  const utcDate = new Date()
  utcDate.setTime(newTZDate?.getTime() + utfcOffset * 60000)
  return utcDate.toISOString()
}

export const getAllDayStartTimeInUTC = (
  date: Date,
  timeZone: string
): string => {
  // For all-day rules, create midnight (00:00:00) in the target timezone
  // Extract date components to avoid timezone confusion and create midnight directly in target timezone
  const midnightInTargetTZ = new TZDate(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0,
    timeZone
  )
  // Convert to UTC by getting the underlying UTC time
  return new Date(midnightInTargetTZ.getTime()).toISOString()
}

// Updates the selectedDate 'date' value to match that of currentDate
// This is used to ensure that the correct date is set when creating a rule
export const formatDateDay = (
  selectedDate: Date,
  currentDate: Date,
  timeZoneId: string
): string => {
  const isNextDay = isCrossDaySelection(currentDate, selectedDate)
  const day = isNextDay
    ? getDayOfYear(currentDate) + 1
    : getDayOfYear(currentDate)

  const updatedDate = setDayOfYear(selectedDate, day)
  return getTimeInUTC(updatedDate, timeZoneId)
}

// Adjusts the date by the passed timeZone.
export const getTimeInTimeZone = (date: string, timeZone: string): Date => {
  if (!date || !timeZone) {
    return null
  }
  const newTZDate = new TZDate(date, timeZone)
  const utfcOffset = newTZDate.getTimezoneOffset()
  const timeZoneDate = new Date()
  timeZoneDate.setTime(newTZDate?.getTime() - utfcOffset * 60000)
  return timeZoneDate
}

export const getFacilityTimeZone = (
  facilitiesData: FacilityTypesQuery,
  selectedFacilityId: string
): string => {
  const facility = facilitiesData?.facilities?.edges?.find(
    (f) => f.node?.id === selectedFacilityId
  )
  if (!facility) {
    return null
  }
  if (facility?.node?.timeZone) {
    return facility?.node?.timeZone
  }
  return tzlookup(facility?.node?.latitude, facility?.node?.longitude)
}

// Returns the selected RuleEventTypes
// Filters the selected options by 'ruleEventTypeOptions'
// Returns the a combination of Manual + ruleEventTypeOptions
export const mapRuleEventsInput = (
  options: OptionI<RuleEventType>[],
  data: ManualIncidentCategoriesQuery
): RuleEventType[] => {
  const eventValues = options?.map((opt) => opt.value)
  const filteredEvents = eventValues.filter((value) =>
    ruleEventTypeOptions.some((opt) => opt.value === value)
  )
  if (getManualCategoryIds(options, data).length > 0) {
    return [...filteredEvents, RuleEventType.Manual]
  }
  return filteredEvents
}

// Returns all the ManualIncidentCategory ids
// Filters the selected options by 'manualIncidentCategories' ids
export const getManualCategoryIds = (
  options: OptionI[],
  data: ManualIncidentCategoriesQuery
) => {
  const eventIds = options?.map((opt) => opt.value)
  const manualCategoryIds =
    data?.manualIncidentCategories?.edges?.map((e) => e?.node?.id) || []
  return eventIds?.filter((id) => manualCategoryIds.includes(id)) || []
}

export const getEventOptions = (
  action: Action,
  data: ManualIncidentCategoriesQuery
) => {
  if (action === Action.Mute) {
    return getMuteEventOptions()
  }
  if (action === Action.EnableAiOperator) {
    return getAiOperatorEventOptions()
  }
  return getEmailOptions(data)
}

export const getMuteEventOptions = () =>
  ruleEventTypeOptions.filter(
    (event) =>
      event.value == RuleEventType.DoorHeldOpen ||
      event.value == RuleEventType.DoorForcedOpen ||
      event.value == RuleEventType.DoorOpenAlarm ||
      event.value == RuleEventType.LineCrossing ||
      event.value == RuleEventType.Tailgating
  )

export const getAiOperatorEventOptions = () =>
  ruleEventTypeOptions.filter(
    (event) =>
      event.value == RuleEventType.DoorHeldOpen ||
      event.value == RuleEventType.DoorForcedOpen ||
      event.value == RuleEventType.DoorOpenAlarm
  )

export const getEmailOptions = (data: ManualIncidentCategoriesQuery) =>
  sortOptions([
    ...getIncidentCategoryOptions(data),
    ...ruleEventTypeOptions.filter(
      (event) => event.value !== RuleEventType.LineCrossing
    ),
  ])

export const getActionOption = (action: Action) => {
  return actionOptions.find((t) => t.value === action)
}

// Returns an combination of RuleEventType and ManualIncidentCategories options
export const getEventTypeOptions = (
  events: RuleEventType[],
  categoryIds: string[],
  data: ManualIncidentCategoriesQuery
): OptionI[] => {
  const possibleCategories =
    data?.manualIncidentCategories?.edges?.map((edge) => edge.node) || []
  const manualCategories =
    possibleCategories?.filter((cat) => categoryIds.includes(cat.id)) || []
  const eventTypes = ruleEventTypeOptions.filter((option) =>
    events?.some((ev) => option.value === ev)
  )
  const manualCategoryOptions =
    manualCategories.map((f) => ({
      label: f?.name,
      value: f?.id,
    })) || []
  return [...manualCategoryOptions, ...eventTypes]
}

export const getOption = (data: OptionT): OptionI => ({
  label: data?.name,
  value: data?.id,
})

export const getFloorsOptions = (devicesData: RuleDevicesQuery): OptionI[] => {
  const floors =
    devicesData?.devices?.edges.map((f) => ({
      label: f?.node?.floor?.name,
      value: f?.node?.floor?.id,
    })) || []
  return floors ? getUniqueValues(floors) : []
}

export const getDeviceOptions = (devicesData: RuleDevicesQuery) => {
  const cameraOptions =
    devicesData?.devices?.edges
      ?.filter((e) => e.node?.type === DeviceType.Camera)
      ?.map((f) => ({
        label: f?.node?.name,
        value: f?.node?.id,
      })) || []
  const doorOptions =
    devicesData?.devices?.edges
      ?.filter((e) => e.node?.type === DeviceType.Door)
      ?.map((f) => ({
        label: f?.node?.name,
        value: f?.node?.id,
      })) || []
  const alarmOptions =
    devicesData?.devices?.edges
      ?.filter((e) => e.node?.type === DeviceType.Alarm)
      ?.map((f) => ({
        label: f?.node?.name,
        value: f?.node?.id,
      })) || []
  return { cameraOptions, doorOptions, alarmOptions }
}

export const getRecipientOptions = (data: RecipientsByRuleQuery): OptionI[] =>
  data?.recipients?.edges.map((f) => ({
    label: f?.node?.email,
    value: f?.node?.id,
  })) || []

export const getTimeDate = (time: string, currentDate: string): string => {
  if (time && currentDate) {
    return `${currentDate?.split('T')[0]}T${time}Z`
  }
  return null
}

export const getSelectedDays = (days: string): DayOptionI[] => {
  if (!days) {
    return []
  }
  const selectedDays = getDaysAsArray(days, 2)
  return DAYS.filter((day) => selectedDays.includes(day.value))
}

// Splits the string by every 'n' characters and returns the result as an array
export const getDaysAsArray = (str: string, n: number): string[] => {
  return str.match(new RegExp('.{1,' + n + '}', 'g'))
}

export const getRuleDeviceIds = (ruleLogics: RuleLogicsT) => {
  return ruleLogics?.edges.flatMap((edge) =>
    edge.node.value ? edge.node.value : []
  )
}

export const isCrossDaySelection = (
  startTime: Date,
  endTime: Date
): boolean => {
  if (!startTime || !endTime) return false

  const start = startTime instanceof Date ? startTime : new Date(startTime)
  const end = endTime instanceof Date ? endTime : new Date(endTime)

  // Special case for midnight (00:00)
  const isMidnight = end.getHours() === 0 && end.getMinutes() === 0

  const startTimeMinutes = start.getHours() * 60 + start.getMinutes()
  const endTimeMinutes = end.getHours() * 60 + end.getMinutes()

  // If end time minutes are less than start time minutes, it's a cross-day selection
  return isMidnight || endTimeMinutes < startTimeMinutes
}

export const adjustEndTimeForCrossDay = (
  startTime: Date,
  endTime: Date
): Date => {
  if (!startTime || !endTime) return endTime

  const adjustedEndTime = new Date(endTime)

  adjustedEndTime.setFullYear(startTime.getFullYear())
  adjustedEndTime.setMonth(startTime.getMonth())
  adjustedEndTime.setDate(startTime.getDate())

  if (isCrossDaySelection(startTime, adjustedEndTime)) {
    adjustedEndTime.setDate(adjustedEndTime.getDate() + 1)
  }

  return adjustedEndTime
}

export const formatDateRange = (startTime: Date, endTime: Date) => {
  if (!startTime || !endTime) {
    return { startDateStr: '', endDateStr: '' }
  }

  const start = startTime instanceof Date ? startTime : new Date(startTime)
  let end = endTime instanceof Date ? endTime : new Date(endTime)

  const isCrossDay = isCrossDaySelection(start, end)

  // If it's a cross-day selection but still has the same date, adjust the end date to the next day
  if (
    isCrossDay &&
    start.getDate() === end.getDate() &&
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  ) {
    end = addDays(end, 1)
  }

  return {
    startDateStr: format(start, 'cccc, LLL do'),
    endDateStr: format(end, 'cccc, LLL do'),
  }
}

export const setValidDevicesForFloorsSelected = (
  form: UseFormMethods<FormInputsT>,
  doorsData: DevicesDoorQuery,
  camerasData: DevicesCameraQuery,
  alarmsData?: DevicesAlarmQuery
) => {
  const { getValues, reset } = form
  const validDoorOptions =
    getValues().doors?.filter((deviceOption) =>
      doorsData?.devices?.edges.some((d) => deviceOption.value === d.node.id)
    ) || []
  const validCameraOptions =
    getValues().cameras?.filter((deviceOption) =>
      camerasData?.devices?.edges.some((d) => deviceOption.value === d.node.id)
    ) || []
  const validAlarmOptions =
    getValues().alarms?.filter((deviceOption) =>
      alarmsData?.devices?.edges.some((d) => deviceOption.value === d.node.id)
    ) || []
  reset({
    ...getValues(),
    doors: validDoorOptions,
    cameras: validCameraOptions,
    alarms: validAlarmOptions,
  })
}

/**
 * Removes the local time offset added when creating a new Date
 *
 * @param date - Date as a string value
 * @returns A date with the local timeZone offset removed
 */
export const getDateWithoutLocalTZOffset = (date: string): Date => {
  const offset = new Date().getTimezoneOffset()
  const endDate = new Date(date)
  return new Date(endDate.getTime() + offset * 60000)
}
